import React, { useEffect, useState } from "react";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTheme } from "@material-ui/core/styles";

import api from "../../services/api";

const MAX_REASONS = 6;

// Monta os query params aceitos por GET /ticketFinalizationReasons/usage a
// partir do MESMO objeto `params` usado em Dashboard/index.js#fetchData:
// { days } ou { date_from, date_to }. Quando só uma das datas vem, envia só
// ela — o backend trata o intervalo como aberto do outro lado, então inventar
// a data que falta distorceria o período.
const buildQueryString = (params) => {
  if (!params) return "";

  if (params.days) {
    return `?days=${params.days}`;
  }

  const query = [];
  if (params.date_from) query.push(`dateFrom=${params.date_from}`);
  if (params.date_to) query.push(`dateTo=${params.date_to}`);

  return query.length ? `?${query.join("&")}` : "";
};

export const ChartsFinalizationReasons = ({ params }) => {
  const theme = useTheme();
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Sem `params` ainda não dá para consultar: mantém o spinner em vez de
    // anunciar "0 no período" com dado que nunca foi buscado.
    if (!params) {
      setLoading(true);
      return;
    }

    let isMounted = true;

    const fetchReasons = async () => {
      setLoading(true);
      try {
        const queryString = buildQueryString(params);
        const { data } = await api.get(
          `/ticketFinalizationReasons/usage${queryString}`
        );
        if (isMounted) {
          setReasons(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Erro ao buscar motivos de finalização:", error);
        if (isMounted) {
          setReasons([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchReasons();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);

  const total = reasons.reduce((acc, item) => acc + (item.count || 0), 0);

  const topReasons = reasons.slice(0, MAX_REASONS);
  const restReasons = reasons.slice(MAX_REASONS);
  const restCount = restReasons.reduce((acc, item) => acc + (item.count || 0), 0);

  const rows = [...topReasons];
  if (restCount > 0) {
    rows.push({ name: "Outros", count: restCount, color: null });
  }

  return (
    <>
      <Typography
        style={{
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: theme.palette.text.primary,
        }}
      >
        Finalizações por motivo
        {!loading && (
          <span style={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
            {" "}
            · {total} no período
          </span>
        )}
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" style={{ padding: "32px 0" }}>
          <CircularProgress size={20} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography
          style={{
            fontSize: 12,
            color: theme.palette.aeros.textTertiary,
            textAlign: "center",
            padding: "32px 0",
          }}
        >
          Nenhum atendimento finalizado com motivo no período
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 14, marginTop: 18 }}>
          {rows.map((reason) => {
            const percent = total > 0 ? (reason.count / total) * 100 : 0;
            const color = reason.color || theme.palette.primary.main;
            return (
              <Box key={reason.name}>
                <Box display="flex" style={{ fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: theme.palette.text.secondary }}>
                    {reason.name}
                  </span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {reason.count} · {percent.toFixed(0)}%
                  </span>
                </Box>
                <Box
                  style={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor:
                      theme.palette.type === "light"
                        ? theme.palette.aeros.surface2
                        : "rgba(255,255,255,0.06)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    style={{
                      height: "100%",
                      width: `${percent}%`,
                      borderRadius: 4,
                      backgroundColor: color,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};

export default ChartsFinalizationReasons;
